import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDistributionLists } from "../hooks";
import UserListItem from "./UserListItem";
import DeleteDistributionUser from "./DeleteDistributionUser";
import { useSelector } from "react-redux";
import AccessibleIconButton from "components/base/AccessibleIconButton";

export default function EditDistributionListPopup({
  open,
  onClose,
  distributionList,
}) {
  const [emails, setEmails] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [deleteUserPopup, setDeleteUserPopup] = useState({
    open: false,
    user: null,
  });
  const {
    updateDistributionListUsers,
    deleteDistributionListUsers,
    getDistributionListDetails,
  } = useDistributionLists();
  const { user } = useSelector((state) => state.auth);
  const currentUserEmail = user?.email;
  const listId = distributionList?.id ?? distributionList?.distributionListId;

  useEffect(() => {
    if (open && listId) {
      loadDistributionListDetails(listId);
    }
  }, [open, listId]);

  const loadDistributionListDetails = async (id) => {
    try {
      setLoadingDetails(true);
      const details = await getDistributionListDetails(id);
      console.log("Distribution List Details:", details);
      setUsers(details || []);
    } catch (error) {
      console.error("Failed to load distribution list details:", error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const validateEmails = (emailString) => {
    const emailList = emailString
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emailList.filter((email) => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      return `Invalid emails: ${invalidEmails.join(", ")}`;
    }

    // Check if current user's email is being added
    const selfEmail = emailList.find(
      (email) => email.toLowerCase() === currentUserEmail?.toLowerCase()
    );
    if (selfEmail) {
      return "You cannot add your own email to the distribution list";
    }

    return "";
  };

  const handleAddEmails = async () => {
    const emailList = emails
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const validEmails = emailList.filter(
      (email) =>
        emailRegex.test(email) &&
        email.toLowerCase() !== currentUserEmail?.toLowerCase()
    );

    if (validEmails.length === 0) {
      return;
    }

    try {
      setLoading(true);
      const allEmails = [
        ...users.map((u) => u.email).filter(Boolean),
        ...validEmails,
      ];
      await updateDistributionListUsers(listId, allEmails);

      // Refetch to get complete user data with avatars and user info
      await loadDistributionListDetails(listId);
      setEmails("");
    } catch (error) {
      console.error("Failed to add emails:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (user) => {
    setDeleteUserPopup({ open: true, user });
  };

  const handleConfirmDelete = async () => {
    const { user } = deleteUserPopup;
    try {
      setLoading(true);
      const userEmail = user.email;
      if (userEmail) {
        await deleteDistributionListUsers(listId, [userEmail]);

        // Remove user from local state instead of refetching
        setUsers((prevUsers) => prevUsers.filter((u) => u.email !== userEmail));
      }
    } catch (error) {
      console.error("Failed to remove user:", error);
    } finally {
      setLoading(false);
      setDeleteUserPopup({ open: false, user: null });
    }
  };

  const handleCancelDelete = () => {
    setDeleteUserPopup({ open: false, user: null });
  };

  const handleKeyDown = async (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      await handleAddEmails();
    }
  };

  const handleClose = () => {
    setEmails("");
    setUsers([]);
    onClose();
  };

  const emailError = emails ? validateEmails(emails) : "";

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "100vw", sm: "70vw", md: "60vw", lg: "50vw" },
    bgcolor: "background.paper",
    boxShadow: 0,
    borderRadius: 2,
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box sx={modalStyle}>
          <Box sx={{ px: 5, py: 4 }}>
            <Typography
              variant="h4"
              component="div"
              color="#406da4"
              fontWeight="600"
            >
              Edit '
              {distributionList?.name || distributionList?.distributionName}'
              Distribution List
            </Typography>
            <AccessibleIconButton
              title="Close"
              onClick={handleClose}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </AccessibleIconButton>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                mt: 3,
                flexWrap: "nowrap",
                alignItems: "center",
              }}
            >
              <TextField
                sx={{ flex: 1, minWidth: 0 }}
                placeholder="Add emails separated by commas"
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                onKeyDown={handleKeyDown}
                error={!!emailError}
                helperText={emailError || ""}
                disabled={loading || loadingDetails}
              />
              <Button
                onClick={handleAddEmails}
                disabled={
                  loading || loadingDetails || !emails.trim() || !!emailError
                }
                startIcon={loading && <CircularProgress size={16} />}
                sx={{ whiteSpace: "nowrap" }}
              >
                Add Emails
              </Button>
            </Box>

            {loadingDetails ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ mt: 4, maxHeight: 400, overflow: "auto", px: 2 }}>
                {users.map((user) => (
                  <UserListItem
                    key={user.id || user.userId || user.email}
                    user={user}
                    onRemove={handleRemoveUser}
                    loading={loading}
                  />
                ))}
                {users.length === 0 && (
                  <Box sx={{ py: 4, textAlign: "center" }}>
                    <Typography variant="body1" color="text.secondary">
                      No users added yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Add email addresses above
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Modal>

      <DeleteDistributionUser
        open={deleteUserPopup.open}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        user={deleteUserPopup.user}
        loading={loading}
        isDistributionList={false}
      />
    </>
  );
}
